// src/pages/Partner/PartnerCohortProgress.jsx
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Activity,
  ArrowLeft,
  Award,
  BarChart3,
  Eye,
  ListChecks,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import {
  Link,
  useOutletContext,
  useParams,
} from "react-router-dom";

import { fetchPartnerCohortDetail } from "../../lib/partner/partnerCohorts";
import { fetchPartnerCohortProgress } from "../../lib/partner/partnerProgress";
import { fetchPartnerLearnerFacilitators } from "../../lib/partner/partnerFacilitators";

function numberValue(value) {
  const parsed = Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : 0;
}

function formatDateTime(value) {
  if (!value) {
    return "Not available";
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

function membershipClasses(status) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";

    case "completed":
      return "bg-blue-100 text-blue-800";

    default:
      return "bg-gray-200 text-gray-700";
  }
}

function positionLabel(row) {
  if (
    !row.current_quest_id &&
    !row.current_subquest_id &&
    !row.current_level_key
  ) {
    return "Not started";
  }

  return [
    row.current_quest_id
      ? `Quest ${row.current_quest_id}`
      : null,

    row.current_subquest_id
      ? `Subquest ${row.current_subquest_id}`
      : null,

    row.current_level_key ||
      null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function activityDescription(row) {
  if (
    row.days_since_activity ===
    null
  ) {
    return "No activity recorded";
  }

  const days =
    numberValue(
      row.days_since_activity
    );

  if (days === 0) {
    return "Active today";
  }

  if (days === 1) {
    return "1 day since activity";
  }

  return `${days} days since activity`;
}

export default function PartnerCohortProgress() {
  const {
    access,
  } = useOutletContext();

  const {
    cohortId,
  } = useParams();

  const [
    cohort,
    setCohort,
  ] = useState(null);

  const [
    learners,
    setLearners,
  ] = useState([]);

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    membershipFilter,
    setMembershipFilter,
  ] = useState("all");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const canViewFacilitatorTags =
    [
      "owner",
      "admin",
      "cohort_manager",
    ].includes(
      access?.partner_role
    );

  const loadProgress =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");

          const [
            cohortRow,
            progressRows,
            facilitatorRows,
          ] =
            await Promise.all([
              fetchPartnerCohortDetail(
                cohortId
              ),

              fetchPartnerCohortProgress(
                cohortId
              ),

              canViewFacilitatorTags
                ? fetchPartnerLearnerFacilitators(
                    cohortId
                  )
                : Promise.resolve(
                    []
                  ),
            ]);

          if (!cohortRow) {
            throw new Error(
              "The cohort could not be found or you do not have access to it."
            );
          }

          const safeProgressRows =
            Array.isArray(
              progressRows
            )
              ? progressRows
              : [];

          const safeFacilitatorRows =
            Array.isArray(
              facilitatorRows
            )
              ? facilitatorRows
              : [];

          const facilitatorByLearnerId =
            new Map(
              safeFacilitatorRows.map(
                (
                  assignment
                ) => [
                  assignment.cohort_learner_id,
                  assignment,
                ]
              )
            );

          const decoratedLearners =
            safeProgressRows.map(
              (learner) => ({
                ...learner,

                facilitator_assignment:
                  canViewFacilitatorTags
                    ? facilitatorByLearnerId.get(
                        learner.cohort_learner_id
                      ) ||
                      null
                    : null,
              })
            );

          setCohort(
            cohortRow
          );

          setLearners(
            decoratedLearners
          );
        } catch (
          loadError
        ) {
          console.error(
            "Partner cohort progress loading failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "The cohort progress could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        cohortId,
        canViewFacilitatorTags,
      ]
    );

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const filteredLearners =
    useMemo(() => {
      const cleanSearch =
        search
          .trim()
          .toLowerCase();

      return learners.filter(
        (learner) => {
          const matchesStatus =
            membershipFilter ===
              "all" ||
            learner.membership_status ===
              membershipFilter;

          if (
            !matchesStatus
          ) {
            return false;
          }

          if (
            !cleanSearch
          ) {
            return true;
          }

          const facilitator =
            canViewFacilitatorTags
              ? learner.facilitator_assignment
              : null;

          return [
            learner.full_name,
            learner.email,
            learner.current_quest_id,
            learner.current_subquest_id,
            learner.current_level_key,
            learner.last_completed_level_title,
            facilitator?.facilitator_full_name,
            facilitator?.facilitator_email,
          ].some(
            (value) =>
              String(
                value ||
                  ""
              )
                .toLowerCase()
                .includes(
                  cleanSearch
                )
          );
        }
      );
    }, [
      learners,
      membershipFilter,
      search,
      canViewFacilitatorTags,
    ]);

  const summary =
    useMemo(() => {
      const totals =
        learners.reduce(
          (
            result,
            learner
          ) => {
            result.levelsCompleted +=
              numberValue(
                learner.levels_completed
              );

            result.outstandingTasks +=
              numberValue(
                learner.tasks_outstanding
              );

            result.progressTotal +=
              numberValue(
                learner.overall_progress_percent
              );

            if (
              learner.last_activity_at
            ) {
              result.activeLearners +=
                1;
            }

            return result;
          },
          {
            levelsCompleted: 0,
            outstandingTasks: 0,
            progressTotal: 0,
            activeLearners: 0,
          }
        );

      return {
        ...totals,

        averageProgress:
          learners.length > 0
            ? totals.progressTotal /
              learners.length
            : 0,
      };
    }, [learners]);

  if (loading) {
    return (
      <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-8 text-center text-sm font-semibold text-[#757575]">
        Loading cohort progress…
      </div>
    );
  }

  if (
    error &&
    !cohort
  ) {
    return (
      <section className="rounded-[28px] border border-red-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-black">
          Progress unavailable
        </h1>

        <p className="mt-3 text-sm text-red-700">
          {error}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={
              loadProgress
            }
            className="inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white"
          >
            <RefreshCw className="mr-2 h-4 w-4" />

            Retry
          </button>

          <Link
            to="/partner/cohorts"
            className="rounded-full border border-[#DDDDDD] px-5 py-3 text-sm font-black"
          >
            Back to cohorts
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <Link
        to={`/partner/cohorts/${cohortId}`}
        className="inline-flex items-center gap-2 text-sm font-black text-[#FF914D] transition hover:text-[#E9782F]"
      >
        <ArrowLeft className="h-4 w-4" />

        Back to cohort
      </Link>

      <header className="mt-6 rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm md:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Partner · Cohort progress
            </p>

            <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] md:text-[42px]">
              {cohort.name}
            </h1>

            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">
              Review learner participation, progression, tasks and recent activity for this cohort.
            </p>
          </div>

          <Link
            to={`/partner/learners?cohort=${cohortId}`}
            className="inline-flex items-center justify-center rounded-full border border-[#FF914D] px-5 py-3 text-sm font-black text-[#FF914D] transition hover:bg-[#FFF4EC]"
          >
            <Users className="mr-2 h-4 w-4" />

            View learners
          </Link>
        </div>

        <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            label="Learners"
            value={
              learners.length
            }
            description="Current cohort records"
            icon={
              Users
            }
          />

          <SummaryCard
            label="Levels completed"
            value={
              summary.levelsCompleted
            }
            description="Across the cohort"
            icon={
              Award
            }
          />

          <SummaryCard
            label="Average progress"
            value={`${summary.averageProgress.toFixed(
              1
            )}%`}
            description="Based on available levels"
            icon={
              BarChart3
            }
          />

          <SummaryCard
            label="Outstanding tasks"
            value={
              summary.outstandingTasks
            }
            description="Unlocked but incomplete"
            icon={
              ListChecks
            }
          />
        </div>
      </header>

      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-[minmax(220px,1fr)_220px]">
          <div>
            <label
              htmlFor="cohort-progress-search"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Search learners
            </label>

            <div className="relative mt-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#999999]" />

              <input
                id="cohort-progress-search"
                value={
                  search
                }
                onChange={(
                  event
                ) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder={
                  canViewFacilitatorTags
                    ? "Search name, email, quest, level, or facilitator"
                    : "Search name, email, quest or level"
                }
                className="w-full rounded-2xl border border-[#DDDDDD] py-3 pl-11 pr-4 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="cohort-progress-status"
              className="block text-xs font-black uppercase tracking-wider text-[#757575]"
            >
              Membership status
            </label>

            <select
              id="cohort-progress-status"
              value={
                membershipFilter
              }
              onChange={(
                event
              ) =>
                setMembershipFilter(
                  event.target.value
                )
              }
              className="mt-2 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            >
              <option value="all">
                All learners
              </option>

              <option value="active">
                Active
              </option>

              <option value="completed">
                Completed
              </option>
            </select>
          </div>
        </div>
      </section>

      {error && (
        <div
          role="alert"
          className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"
        >
          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={
              loadProgress
            }
            className="inline-flex flex-none items-center rounded-full border border-red-300 px-3 py-2"
          >
            <RefreshCw className="mr-2 h-4 w-4" />

            Retry
          </button>
        </div>
      )}

      {filteredLearners.length ===
      0 ? (
        <section className="mt-6 rounded-[28px] border border-dashed border-[#FFB98B] bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
            <Users className="h-7 w-7" />
          </div>

          <h2 className="mt-5 text-xl font-black">
            No learner progress found
          </h2>

          <p className="mx-auto mt-2 max-w-lg text-sm leading-relaxed text-[#757575]">
            No learners match the current filters for this cohort.
          </p>
        </section>
      ) : (
        <section className="mt-6 overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table
              className={[
                "divide-y divide-[#EFEFEF]",
                canViewFacilitatorTags
                  ? "min-w-[1700px]"
                  : "min-w-[1480px]",
              ].join(" ")}
            >
              <thead className="bg-[#FFF9F5]">
                <tr>
                  <TableHeading>
                    Learner
                  </TableHeading>

                  {canViewFacilitatorTags && (
                    <TableHeading>
                      Facilitator
                    </TableHeading>
                  )}

                  <TableHeading>
                    Status
                  </TableHeading>

                  <TableHeading>
                    Current position
                  </TableHeading>

                  <TableHeading>
                    Progress
                  </TableHeading>

                  <TableHeading>
                    XP
                  </TableHeading>

                  <TableHeading>
                    Tasks
                  </TableHeading>

                  <TableHeading>
                    Latest level
                  </TableHeading>

                  <TableHeading>
                    Activity
                  </TableHeading>

                  <TableHeading>
                    Behaviour / risk
                  </TableHeading>

                  <TableHeading align="right">
                    Action
                  </TableHeading>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#EFEFEF]">
                {filteredLearners.map(
                  (learner) => {
                    const progress =
                      numberValue(
                        learner.overall_progress_percent
                      );

                    return (
                      <tr
                        key={
                          learner.cohort_learner_id
                        }
                        className="align-top transition hover:bg-[#FFFDFB]"
                      >
                        <td className="px-5 py-4">
                          <p className="font-black text-[#333333]">
                            {learner.full_name ||
                              "Learner"}
                          </p>

                          <p className="mt-1 text-xs font-semibold text-[#757575]">
                            {learner.email}
                          </p>
                        </td>

                        {canViewFacilitatorTags && (
                          <td className="px-5 py-4">
                            <FacilitatorDetails
                              assignment={
                                learner.facilitator_assignment
                              }
                            />
                          </td>
                        )}

                        <td className="px-5 py-4">
                          <span
                            className={[
                              "inline-flex rounded-full px-3 py-1 text-xs font-black capitalize",
                              membershipClasses(
                                learner.membership_status
                              ),
                            ].join(
                              " "
                            )}
                          >
                            {
                              learner.membership_status
                            }
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <p className="text-sm font-black">
                            {positionLabel(
                              learner
                            )}
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            Updated{" "}
                            {formatDateTime(
                              learner.current_position_updated_at
                            )}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <div className="w-40">
                            <div className="flex items-center justify-between text-xs font-black">
                              <span>
                                {
                                  learner.levels_completed
                                }{" "}
                                of{" "}
                                {
                                  learner.total_levels
                                }
                              </span>

                              <span>
                                {progress.toFixed(
                                  1
                                )}
                                %
                              </span>
                            </div>

                            <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#EFEFEF]">
                              <div
                                className="h-full rounded-full bg-[#FF914D]"
                                style={{
                                  width: `${Math.min(
                                    Math.max(
                                      progress,
                                      0
                                    ),
                                    100
                                  )}%`,
                                }}
                              />
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <p className="text-lg font-black">
                            {numberValue(
                              learner.total_xp
                            ).toLocaleString(
                              "en-ZA"
                            )}
                          </p>

                          <p className="text-xs text-[#757575]">
                            Total XP
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p className="text-sm font-black">
                            {
                              learner.tasks_completed
                            }{" "}
                            of{" "}
                            {
                              learner.tasks_total
                            }{" "}
                            completed
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            {
                              learner.tasks_outstanding
                            }{" "}
                            outstanding
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p className="max-w-[220px] text-sm font-black">
                            {learner.last_completed_level_title ||
                              "Not started"}
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            {formatDateTime(
                              learner.last_completed_at
                            )}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p className="inline-flex items-center text-sm font-black">
                            <Activity className="mr-2 h-4 w-4 text-[#FF914D]" />

                            {activityDescription(
                              learner
                            )}
                          </p>

                          <p className="mt-2 text-xs text-[#757575]">
                            Last activity:{" "}
                            {formatDateTime(
                              learner.last_activity_at
                            )}
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            Last login:{" "}
                            {formatDateTime(
                              learner.last_login_at
                            )}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <p className="text-sm font-black">
                            {learner.behavioural_status ||
                              "Not calculated"}
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            Risk:{" "}
                            {learner.risk_status ||
                              "Not available"}
                          </p>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <Link
                            to={`/partner/learners/${learner.cohort_learner_id}`}
                            className="inline-flex items-center rounded-full border border-[#FF914D] px-3 py-2 text-xs font-black text-[#FF914D] transition hover:bg-[#FFF4EC]"
                          >
                            <Eye className="mr-1.5 h-3.5 w-3.5" />

                            View details
                          </Link>
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function FacilitatorDetails({
  assignment,
}) {
  if (
    !assignment
      ?.facilitator_partner_member_id
  ) {
    return (
      <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-black text-gray-600">
        Unassigned
      </span>
    );
  }

  return (
    <div>
      <p className="text-sm font-black text-[#333333]">
        {assignment.facilitator_full_name ||
          "Facilitator"}
      </p>

      {assignment.facilitator_email && (
        <p className="mt-1 text-xs font-semibold text-[#757575]">
          {assignment.facilitator_email}
        </p>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  description,
  icon: Icon,
}) {
  return (
    <article className="rounded-[22px] border border-[#EFEFEF] bg-[#FFFDFB] p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>

          <p className="mt-3 text-3xl font-black">
            {value}
          </p>

          <p className="mt-1 text-sm text-[#757575]">
            {description}
          </p>
        </div>

        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </article>
  );
}

function TableHeading({
  children,
  align = "left",
}) {
  return (
    <th
      scope="col"
      className={[
        "whitespace-nowrap px-5 py-3 text-xs font-black uppercase tracking-wider text-[#757575]",
        align === "right"
          ? "text-right"
          : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}